import { useMemo } from 'react';
import { SelectField } from '@/components/forms/SelectField';
import { MultiSelectField } from '@/components/forms/MultiSelectField';
import { TextField } from '@/components/forms/TextField';
import {
  FIELD_LABELS,
  MAX_SECTOR_OTHER_LENGTH,
  MAX_SUBSECTORS,
  SECTOR_OTHER_NAME,
} from '../constants';
import { useSectors } from '../hooks/useSectors';

/**
 * The sector half of a startup profile: one primary sector plus up to
 * `MAX_SUBSECTORS` of that sector's own subsectors.
 *
 * Held as one value rather than two independent fields because they are not
 * independent - a subsector only exists under a sector, so changing the sector
 * invalidates whatever was chosen below it. Emitting both together is what lets
 * this control clear the subsectors itself, instead of the caller
 * re-implementing that reset with a `useRef` previous-value guard.
 *
 * Used by the founder's profile editor and the admin console's correction form.
 * The create wizard is NOT one of the callers: `steps/sections/SectorStage.tsx`
 * still runs its own cascade with its own `useRef` guards, and migrating it was
 * out of scope for MYN-132. Two implementations of this rule exist today.
 */
export interface SectorSelection {
  sectorId?: number;
  subsectorIds: number[];
  /**
   * The founder's own sector, only while the sector is "Other" (which has no
   * subsectors, UX sheet r104). Dropped with the subsectors on a sector change.
   */
  sectorOther?: string;
}

export interface SectorFieldsProps {
  value: SectorSelection;
  onChange: (next: SectorSelection) => void;
  sectorLabel: string;
  subsectorsLabel: string;
  labelVariant?: 'bold' | 'caps';
  error?: string;
  sectorOtherError?: string;
}

/**
 * Sector + subsectors, controlled by value/onChange so it works with any form
 * library (or none). Reference data comes from `useSectors`, which is
 * prefetched on dashboard mount and cached for a day.
 */
export function SectorFields({
  value,
  onChange,
  sectorLabel,
  subsectorsLabel,
  labelVariant = 'caps',
  error,
  sectorOtherError,
}: SectorFieldsProps) {
  const { data: sectors = [] } = useSectors();

  const sectorOptions = useMemo(
    () => sectors.map((sector) => ({ value: String(sector.id), label: sector.name })),
    [sectors],
  );

  const selectedSector = sectors.find((sector) => sector.id === value.sectorId);
  const isOtherSector = selectedSector?.name === SECTOR_OTHER_NAME;
  const subsectorOptions = useMemo(
    () =>
      (selectedSector?.subsectors ?? []).map((subsector) => ({
        value: String(subsector.id),
        label: subsector.name,
      })),
    [selectedSector],
  );

  const atCap = value.subsectorIds.length >= MAX_SUBSECTORS;

  function toggleSubsector(optionValue: string) {
    const id = Number(optionValue);
    const next = value.subsectorIds.includes(id)
      ? value.subsectorIds.filter((existing) => existing !== id)
      : [...value.subsectorIds, id];
    onChange({ ...value, subsectorIds: next.slice(0, MAX_SUBSECTORS) });
  }

  return (
    <>
      <SelectField
        label={sectorLabel}
        labelVariant={labelVariant}
        options={sectorOptions}
        // No placeholder on purpose: `Select` turns one into a SELECTABLE empty
        // option, and there is no way to clear a sector through this API - the
        // Backend only rewrites the sector rows when an id is present. Offering
        // the choice would let an admin blank the field, be told the save
        // succeeded, and have an audit row written for nothing.
        value={value.sectorId === undefined ? '' : String(value.sectorId)}
        error={error}
        // A new sector makes the current subsectors meaningless, so they go
        // with it - leaving them behind submits subsectors that do not belong
        // to the sector alongside them.
        onChange={(event) =>
          onChange({
            sectorId: event.target.value ? Number(event.target.value) : undefined,
            subsectorIds: [],
          })
        }
      />
      {isOtherSector ? (
        <TextField
          label={FIELD_LABELS.sectorOther}
          labelVariant={labelVariant}
          maxLength={MAX_SECTOR_OTHER_LENGTH}
          value={value.sectorOther ?? ''}
          onChange={(event) => onChange({ ...value, sectorOther: event.target.value })}
          error={sectorOtherError}
        />
      ) : (
        <MultiSelectField
          label={subsectorsLabel}
          labelVariant={labelVariant}
          disabled={subsectorOptions.length === 0}
          options={subsectorOptions}
          selectedValues={value.subsectorIds.map(String)}
          onToggle={toggleSubsector}
          isOptionDisabled={(optionValue) =>
            atCap && !value.subsectorIds.includes(Number(optionValue))
          }
        />
      )}
    </>
  );
}
